import type { ITransaction, ITxnStatus } from './types';

import type { SessionVerdict } from 'views/design-system';

/** Money in the transaction's own currency; crypto keeps its precision. */
/**
 * A crypto quantity, WITHOUT routing it through Number().
 *
 * The amount is stored as a string precisely so a raw token quantity survives:
 * an 18-decimal ERC-20 balance held as an integer runs past
 * Number.MAX_SAFE_INTEGER (9,007,199,254,740,991), and `Number(amt)` silently
 * rounds it — the storage decision was being undone by the one function that
 * read it. It also hard-coded 5 decimal places for BTC, which truncates
 * satoshis: 0.00000001 BTC rendered as "0.00000".
 *
 * So the digits are formatted as TEXT. Trailing zeros are trimmed because a
 * quantity is not a price — "0.25 BTC", not "0.25000000 BTC" — but nothing is
 * ever rounded away.
 */
const cryptoQty = (amt: string | number): string => {
	const raw = String(amt ?? '').trim();
	if (!raw || !/^-?\d*\.?\d+$/.test(raw)) return raw || '0';
	const [whole, frac = ''] = raw.split('.');
	const grouped = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	const trimmed = frac.replace(/0+$/, '');
	return trimmed ? `${grouped}.${trimmed}` : grouped;
};

/** Currencies with no minor unit — rendering "¥1,200.00" is wrong, not precise. */
const ZERO_DECIMAL = ['JPY', 'KRW', 'VND', 'CLP', 'ISK'];

export const money = (
	amt: string | number,
	ccy: string,
	kind?: 'fiat' | 'crypto'
): string => {
	/* Driven by the declared KIND when there is one, not by a hard-coded ticker
	   list. The old version knew BTC, ETH, USDT and USDC and treated every other
	   token — every new listing a trading client adds — as a fiat currency with
	   a two-decimal cap, which silently rounded it. */
	const isCrypto =
		kind === 'crypto' || (!kind && ['BTC', 'ETH', 'USDT', 'USDC'].includes(ccy));
	if (isCrypto) return `${cryptoQty(amt)} ${ccy}`;

	const n = Number(amt);
	const sym: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
	const prefix = sym[ccy] ?? `${ccy} `;
	const digits = ZERO_DECIMAL.includes(ccy) ? 0 : 2;
	return `${prefix}${n.toLocaleString(undefined, {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	})}`;
};

/**
 * A transaction's value as one legible string.
 *
 * A payment is its own amount. A TRADE is "sent → received", because the whole
 * point is that the two differ — showing only one side of a BTC-for-USD trade
 * describes half of what happened, and showing the fiat-equivalent alone hides
 * that any crypto was involved at all.
 */
export const legsLabel = (t: {
	transactionKind?: string;
	amount: string | number;
	currency: string;
	currencyKind?: 'fiat' | 'crypto';
	legs?: {
		sent?: { amount: string | number; currency: string; currencyKind?: 'fiat' | 'crypto' };
		received?: { amount: string | number; currency: string; currencyKind?: 'fiat' | 'crypto' };
	};
}): string => {
	const s = t.legs?.sent;
	const r = t.legs?.received;
	if (t.transactionKind === 'trade' && s && r) {
		return `${money(s.amount, s.currency, s.currencyKind)} → ${money(
			r.amount,
			r.currency,
			r.currencyKind
		)}`;
	}
	return money(t.amount, t.currency, t.currencyKind);
};

export const usd = (n: number): string => `$${Math.round(n).toLocaleString()}`;

const pad = (x: number) => String(x).padStart(2, '0');

export const hms = (ms: number): string => {
	const d = new Date(ms);
	return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const hm = (ms: number): string => hms(ms).slice(0, 5);

/** "42s" · "7m" · "2h 48m" */
export const ago = (ms: number, now = Date.now()): string => {
	const s = Math.max(0, Math.round((now - ms) / 1000));
	if (s < 60) return `${s}s`;
	const m = Math.floor(s / 60);
	if (m < 60) return `${m}m`;
	return `${Math.floor(m / 60)}h ${m % 60}m`;
};

/** Time until a deadline; negative once it has passed ("-30m"). */
export const until = (ms: number, now = Date.now()): string => {
	const s = Math.round((ms - now) / 1000);
	const a = Math.abs(s);
	const f =
		a < 60 ? `${a}s` : a < 3600 ? `${Math.floor(a / 60)}m` : `${Math.floor(a / 3600)}h ${Math.floor((a % 3600) / 60)}m`;
	return s < 0 ? `-${f}` : f;
};

export const isoStamp = (iso: string): string => `${iso.replace('T', ' ').slice(0, 19)}Z`;

/** Didit status → the StatusChip verdict the rest of the product uses. */
export const verdictOf = (st: ITxnStatus): SessionVerdict =>
	({ APPROVED: 'ok', IN_REVIEW: 'rev', DECLINED: 'rej', AWAITING_USER: 'strt' } as const)[st] ?? 'ns';

/** The coloured left edge class on a row. */
export const edgeOf = (st: ITxnStatus): string =>
	({ APPROVED: 'e-ok', IN_REVIEW: 'e-rev', DECLINED: 'e-rej', AWAITING_USER: 'e-run' } as const)[st] ?? '';

export const statusLabel = (st: ITxnStatus): string =>
	({ APPROVED: 'APPROVED', IN_REVIEW: 'IN REVIEW', DECLINED: 'DECLINED', AWAITING_USER: 'AWAITING USER' } as const)[
		st
	] ?? st;

/** The rule name, shortened for a chip: the part before the em dash. */
export const shortRule = (name: string): string =>
	((name.split(' — ')[0] ?? name).split(' (')[0] ?? name)
		.replace('Multi-accounting', 'Shared device')
		.replace('Sanctioned wallet exposure', 'Sanctioned wallet');

export const corridor = (t: ITransaction): string =>
	`${t.subjectCountry || '??'} ${t.direction === 'INBOUND' ? '←' : '→'} ${t.counterpartyCountry || '—'}`;

export const HIGH_RISK_JURISDICTIONS = ['IR', 'KP', 'MM', 'SY', 'AF'];

export const dateLine = (): string =>
	new Date().toLocaleDateString(undefined, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});

/**
 * THREE STATES, NOT TWO: a fee nobody supplied, a fee explicitly reported as
 * zero, and a fee that was charged.
 *
 * `feePreferredCurrencyAmount` is the SUMMED, COMPUTED figure, and it is `0`
 * both when nothing was supplied anywhere and when a client explicitly sent
 * `feeAmount: 0` — a real fact meaning "we checked, this institution charged
 * nothing". A truthiness test on that number collapses the two, so an honestly
 * reported zero vanished from the screen exactly like missing data.
 *
 * The distinction survives storage and needs no model change: the raw
 * `feeAmount` is a String defaulting to `null`, so `null` (never supplied) and
 * `"0"` (explicitly zero) are already different in what is stored. Only the
 * display's check was wrong. Derived here ONCE and imported everywhere rather
 * than re-derived per surface, because three independent re-derivations is three
 * chances to get this exact distinction wrong again.
 */
export type IFeeState = 'unsupplied' | 'zero' | 'present';

export const feeState = (txn: {
	feeAmount?: string | number | null;
	feePreferredCurrencyAmount?: number;
	legs?: {
		sent?: { feeAmount?: string | number | null };
		received?: { feeAmount?: string | number | null };
	};
}): IFeeState => {
	const raw = [txn.feeAmount, txn.legs?.sent?.feeAmount, txn.legs?.received?.feeAmount];
	if (raw.every(v => v === undefined || v === null || v === '')) return 'unsupplied';
	return (txn.feePreferredCurrencyAmount ?? 0) > 0 ? 'present' : 'zero';
};

/**
 * The one hover sentence every surface shows for the same transaction.
 *
 * Shared so the queue row, the tape and the evidence cell cannot drift into
 * three different phrasings of the same three figures. Always names the TOTAL as
 * "reported, not scored": the engine reads the PRINCIPAL — a fee is the
 * institution's revenue, not value moved — and a reader seeing two money figures
 * has to be told which one drove the decision.
 */
export const feeTitle = (txn: {
	amount: string | number;
	currency: string;
	currencyKind?: 'fiat' | 'crypto';
	transactionKind?: string;
	preferredCurrencyAmount?: number;
	preferredCurrencyTotalAmount?: number;
	feeAmount?: string | number | null;
	feeCurrency?: string | null;
	feePreferredCurrencyAmount?: number;
	legs?: {
		sent?: {
			amount: string | number;
			currency: string;
			currencyKind?: 'fiat' | 'crypto';
			feeAmount?: string | number | null;
			feeCurrency?: string | null;
		};
		received?: {
			amount: string | number;
			currency: string;
			currencyKind?: 'fiat' | 'crypto';
			feeAmount?: string | number | null;
			feeCurrency?: string | null;
		};
	};
}): string => {
	const state = feeState(txn);
	const parts: string[] = [];

	const s = txn.legs?.sent;
	const r = txn.legs?.received;
	if (txn.transactionKind === 'trade' && s && r) {
		parts.push(`sent ${money(s.amount, s.currency, s.currencyKind)}`);
		parts.push(`received ${money(r.amount, r.currency, r.currencyKind)}`);
		if (txn.preferredCurrencyAmount !== undefined)
			parts.push(
				`principal $${txn.preferredCurrencyAmount.toLocaleString()} (the greater leg — what every threshold rule read)`
			);
	} else {
		parts.push(`amount ${money(txn.amount, txn.currency, txn.currencyKind)}`);
	}

	if (state === 'unsupplied') {
		parts.push('fee not reported');
		return parts.join(' · ');
	}
	if (state === 'zero') {
		parts.push('$0.00 — no fee charged');
		return parts.join(' · ');
	}

	/* Per leg when a leg carries its own — a venue taking its cut only on the
	   receiving side is a common shape, and one combined number gives the reader
	   no way to tell which side it came from. */
	if (s?.feeAmount) parts.push(`sent-leg fee ${money(s.feeAmount, s.feeCurrency ?? s.currency)}`);
	if (r?.feeAmount)
		parts.push(`received-leg fee ${money(r.feeAmount, r.feeCurrency ?? r.currency)}`);

	parts.push(`total fee $${(txn.feePreferredCurrencyAmount ?? 0).toLocaleString()}`);
	if (txn.preferredCurrencyTotalAmount)
		parts.push(
			`total $${txn.preferredCurrencyTotalAmount.toLocaleString()} (reported, not scored)`
		);
	return parts.join(' · ');
};
